import {
  captureObservabilityError,
  useAnalytics,
  useAuthentication,
  useQueryParam,
  withLoginSubmit,
} from '@psyomics/ui-common';
import { LoginForm, MfaFieldForm } from '@psyomics/ui-components';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { getConfig } from '../config';
import { postJson } from '../features/mfa-setup/mfa-client';
import { QueryError, useReferralInviteQuery } from '../features/referral/hooks/use-referral-invite-query';

type Challenge = {
  session: string;
  username: string;
};

type Invite = ReturnType<typeof useReferralInviteQuery>[0];

const useReferralInviteAnalytics = ({
  analytics,
  authSystemId,
  error,
  invite,
  invitePublicId,
  isAuthenticated,
}: {
  analytics: ReturnType<typeof useAnalytics>;
  authSystemId?: string;
  error?: QueryError | null;
  invite: Invite;
  invitePublicId?: string;
  isAuthenticated: boolean;
}) => {
  useEffect(() => {
    if (error) {
      captureObservabilityError(
        `Referral invite query failed for invite public id ${invitePublicId}: ${error.message}`
      );
    }
  }, [error, invitePublicId]);

  useEffect(() => {
    if (!isAuthenticated || !authSystemId) return;

    const serviceCode = invite?.referralSession?.referral?.service?.code;
    const timestamp = new Date().toISOString();

    const properties = {
      last_login: timestamp,
      ...(invitePublicId && {
        current_invite: invitePublicId,
      }),
      ...(serviceCode && {
        current_service: serviceCode,
      }),
    };

    analytics.identify({ distinctId: authSystemId, properties });
  }, [analytics, invite, invitePublicId, isAuthenticated, authSystemId]);
};

const MfaChallengeStep = ({
  apiUrl,
  challenge,
  getAuthSessionContext,
  onComplete,
  organisation,
  postLogin,
  redirect,
}: {
  readonly challenge: Challenge;
  readonly onComplete: () => void;
  readonly apiUrl: string;
  readonly getAuthSessionContext: () => { invitePublicId?: string };
  readonly organisation?: string;
  readonly postLogin: ReturnType<typeof useAuthentication>['postLogin'];
  readonly redirect?: string;
}) => {
  const { t } = useTranslation();

  const handleSubmit = async (code: string) => {
    try {
      const response = await postJson(`${apiUrl}/auth/mfa/login`, {
        code,
        context: getAuthSessionContext(),
        ...(organisation && { organisation }),
        session: challenge.session,
        username: challenge.username,
      });

      const payload = await response.json().catch(() => null);
      if (!payload?.success) {
        throw new Error('mfa login failed');
      }

      postLogin(payload, redirect);
      onComplete();
    } catch {
      throw new Error('mfa login failed');
    }
  };

  return (
    <MfaFieldForm
      autoComplete="one-time-code"
      buttonLabel={t('auth.login.mfa.submit')}
      description={t('auth.common.mfa.codeDescription')}
      errorRequired={t('auth.common.errors.confirmationCodeMissing')}
      inputId="mfaCode"
      inputLabel={t('auth.login.mfa.codeInputLabel')}
      inputMode="numeric"
      onSubmit={handleSubmit}
      title={t('auth.login.mfa.title')}
    />
  );
};

export const SelfHostedLogin = ({ forgotPasswordUrl }: { readonly forgotPasswordUrl: string }) => {
  const { patientApiUrl } = getConfig();
  const invitePublicId = useQueryParam('invitePublicId') ?? undefined;
  const { authSystemId, isAuthenticated, postLogin } = useAuthentication();
  const { organisation } = useParams();

  const getAuthSessionContext = useCallback(
    () => ({
      invitePublicId,
    }),
    [invitePublicId]
  );

  const redirect = useQueryParam('redirect') ?? undefined;
  const analytics = useAnalytics();
  const [invite, , error] = useReferralInviteQuery(invitePublicId);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const LoginWithSubmit = withLoginSubmit(LoginForm);

  useReferralInviteAnalytics({
    analytics,
    authSystemId,
    error,
    invite,
    invitePublicId,
    isAuthenticated,
  });

  if (challenge) {
    return (
      <div className="form-container">
        <MfaChallengeStep
          apiUrl={patientApiUrl}
          challenge={challenge}
          getAuthSessionContext={getAuthSessionContext}
          onComplete={() => setChallenge(null)}
          organisation={organisation}
          postLogin={postLogin}
          redirect={redirect}
        />
      </div>
    );
  }

  return (
    <LoginWithSubmit
      apiUrl={patientApiUrl}
      forgotPasswordUrl={forgotPasswordUrl}
      getAuthSessionContext={getAuthSessionContext}
      onMfaChallenge={({ session, username }) => setChallenge({ session, username })}
    />
  );
};
